import { inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UnificationAPIService } from '@shared/services/unification.api.service';
import { configHubEnvironment } from '../../config-hub.environment';
import { isConfigHubApiUrl } from '../utils/api-url.util';
import { isRecord } from '../models/form-value.model';
import { AuthService } from '../services/auth.service';

interface AuthErrorBody {
  success: false;
  statusCode: number | string;
  message?: string;
  error?: string;
}

function isAuthErrorBody(body: unknown): body is AuthErrorBody {
  return isRecord(body)
    && body['success'] === false
    && (body['statusCode'] === 401 || body['statusCode'] === '401');
}

/**
 * Attaches NextGen Bearer to Config Hub API calls and normalizes Unify ResponseJson
 * envelopes to the legacy { success, data } shape expected by ConfigHub services.
 */
@Injectable()
export class ConfigHubAuthInterceptor implements HttpInterceptor {
  private readonly unificationApi = inject(UnificationAPIService);
  private readonly authService = inject(AuthService);

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!isConfigHubApiUrl(request.url)) {
      return next.handle(request);
    }

    const isDraftSaveRequest = request.url.includes('/draft');
    const authReq = this.addTokenHeader(request);

    return next.handle(authReq).pipe(
      map((event: HttpEvent<unknown>) => {
        if (!(event instanceof HttpResponse)) {
          return event;
        }

        const body = event.body;
        if (isAuthErrorBody(body) && !isDraftSaveRequest) {
          this.authService.logoutWithRedirect(
            body.message || body.error || 'Your session has expired. Please login again.'
          );
          return event;
        }

        if (configHubEnvironment.useUnifyBackend && isRecord(body) && !('success' in body)) {
          const payload = body['data'] ?? body['Data'];
          if (payload !== undefined) {
            return event.clone({
              body: {
                success: true,
                data: payload,
                events: body['events'] ?? body['Events'],
                message: undefined,
              },
            });
          }
        }

        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        if (!(error instanceof HttpErrorResponse)) {
          return throwError(() => error);
        }

        if (error.status === 401 && !isDraftSaveRequest) {
          this.authService.logoutWithRedirect(
            'Your session has expired. Please login again.'
          );
          return throwError(() => error);
        }

        if (error.status === 403) {
          return throwError(() => error);
        }

        return throwError(() => error);
      })
    );
  }

  private addTokenHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = this.unificationApi.authToken;
    if (!token) {
      return request;
    }
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }
}
